import { z } from "zod";

/**
 * Shared DTO cho Member Import & Staging — nhập danh sách thành viên → staging → confirm → invite
 * (US-E2.F3-0081 · @UC0027, Phase 3b MVP-locked).
 *
 * Một nguồn sự thật shape api ↔ mobile (DRY):
 *  - POST /v1/groups/:id/member-imports → tạo batch staging từ rows (name + email/phone),
 *    source=manual|csv|ocr. Row lỗi vẫn được lưu (cờ `valid=false` + errors) — hiển thị, KHÔNG chặn nhập.
 *  - PATCH/DELETE /v1/groups/:id/member-imports/:batchId/rows/:rowId → sửa/xoá row TỰ DO trước confirm
 *    (staging KHÁC ledger append-only).
 *  - POST /v1/groups/:id/member-imports/:batchId/confirm → mỗi row hợp lệ có email → tạo Invitation
 *    pending (reuse invite engine 0023, no-SMS). Report per-row (created/skipped + reason).
 *
 * BẢO MẬT (invariants):
 *  - RBAC OWNER/ADMIN (GroupRoleGuard) — member 403.
 *  - Batch bound group (IDOR): batchId/rowId phải thuộc group đang truy cập → cross-group 404.
 *  - PII: email/phone import KHÔNG log raw (redact ở app layer). validate biên (C5) — reject over-post.
 *  - C1 no-SMS: kênh mời DUY NHẤT = email. Row chỉ có phone → normalize để match/dedup, KHÔNG gửi.
 *
 * 📸 OCR seam: source='ocr' nhận **text đã trích sẵn** (client/provider ngoài) → cùng staging flow.
 *    Provider OCR THẬT (Google Vision/Textract) DEFER config (PO chốt 02/07) — chỉ seam, KHÔNG build.
 */

/** Số row tối đa trong 1 batch import (over-post/DoS guard — nới hơn invite-email 50 vì import lô). */
export const IMPORT_ROWS_MAX = 200;

/** Số row tối thiểu trong 1 batch (ít nhất 1 — AC1). */
export const IMPORT_ROWS_MIN = 1;

/** Độ dài tối đa các field text tại biên (chặn input phình to). */
export const IMPORT_NAME_MAX_LENGTH = 120;
export const IMPORT_EMAIL_MAX_LENGTH = 254;
export const IMPORT_PHONE_MAX_LENGTH = 20;

/** Nguồn nhập danh sách. 'ocr' = text đã trích sẵn (provider ngoài — seam, KHÔNG build provider). */
export const IMPORT_SOURCE = {
  MANUAL: "manual",
  CSV: "csv",
  OCR: "ocr",
} as const;

export const importSourceSchema = z.enum([
  IMPORT_SOURCE.MANUAL,
  IMPORT_SOURCE.CSV,
  IMPORT_SOURCE.OCR,
]);
export type ImportSource = z.infer<typeof importSourceSchema>;

/** Trạng thái batch: draft (đang sửa staging) | confirmed (đã confirm → invite). */
export const IMPORT_BATCH_STATUS = {
  DRAFT: "draft",
  CONFIRMED: "confirmed",
} as const;

export const importBatchStatusSchema = z.enum([
  IMPORT_BATCH_STATUS.DRAFT,
  IMPORT_BATCH_STATUS.CONFIRMED,
]);
export type ImportBatchStatus = z.infer<typeof importBatchStatusSchema>;

/** Lý do skip khi confirm (report per-row). */
export const IMPORT_SKIP_REASON = {
  INVALID: "invalid", // row không hợp lệ (validate cờ lỗi)
  ALREADY_MEMBER: "already_member", // email đã là member active
  ALREADY_PENDING: "already_pending", // đã có invitation pending (dedup)
  SELF: "self", // email của chính admin đang confirm
  PHONE_ONLY_NO_CHANNEL: "phone_only_no_channel", // chỉ có phone → no-SMS, không kênh gửi
} as const;

export const importSkipReasonSchema = z.enum([
  IMPORT_SKIP_REASON.INVALID,
  IMPORT_SKIP_REASON.ALREADY_MEMBER,
  IMPORT_SKIP_REASON.ALREADY_PENDING,
  IMPORT_SKIP_REASON.SELF,
  IMPORT_SKIP_REASON.PHONE_ONLY_NO_CHANNEL,
]);
export type ImportSkipReason = z.infer<typeof importSkipReasonSchema>;

/**
 * Một row nhập vào staging. name BẮT BUỘC; email/phone OPTIONAL nhưng phải có ÍT NHẤT một
 * (name-only vô nghĩa để mời). Chuẩn hoá/validate sâu (E.164, format) làm ở domain (member-import.ts)
 * — biên chỉ chặn shape + over-post + refine "có ít nhất 1 định danh".
 */
export const importRowInputSchema = z
  .object({
    name: z.string().trim().min(1, "Tên không được rỗng.").max(IMPORT_NAME_MAX_LENGTH),
    email: z.string().trim().max(IMPORT_EMAIL_MAX_LENGTH).optional(),
    phone: z.string().trim().max(IMPORT_PHONE_MAX_LENGTH).optional(),
  })
  .strict()
  .refine((r) => (r.email && r.email.length > 0) || (r.phone && r.phone.length > 0), {
    message: "Cần email hoặc số điện thoại.",
  });
export type ImportRowInput = z.infer<typeof importRowInputSchema>;

/**
 * Body POST /v1/groups/:id/member-imports — tạo batch staging.
 * - source BẮT BUỘC (manual|csv|ocr). rows 1..200. `.strict()` chặn over-post (KHÔNG nhận groupId/status).
 */
export const createImportBatchRequestSchema = z
  .object({
    source: importSourceSchema,
    rows: z
      .array(importRowInputSchema)
      .min(IMPORT_ROWS_MIN, "Cần ít nhất 1 dòng.")
      .max(IMPORT_ROWS_MAX, `Tối đa ${IMPORT_ROWS_MAX} dòng mỗi lần.`),
  })
  .strict();
export type CreateImportBatchRequest = z.infer<typeof createImportBatchRequestSchema>;

/**
 * Body PATCH row — sửa name/email/phone (tự do trước confirm). Ít nhất 1 field. `.strict()` chặn over-post.
 * KHÔNG có refine "ít nhất 1 định danh" ở đây (patch từng phần); server merge rồi re-validate row.
 */
export const patchImportRowRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(IMPORT_NAME_MAX_LENGTH).optional(),
    email: z.string().trim().max(IMPORT_EMAIL_MAX_LENGTH).optional(),
    phone: z.string().trim().max(IMPORT_PHONE_MAX_LENGTH).optional(),
  })
  .strict()
  .refine((r) => r.name !== undefined || r.email !== undefined || r.phone !== undefined, {
    message: "Cần ít nhất 1 field để cập nhật.",
  });
export type PatchImportRowRequest = z.infer<typeof patchImportRowRequestSchema>;

/** Một row trong response staging — PII (email/phone) trả cho OWNER/ADMIN để review/sửa (không public). */
export const importRowSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    valid: z.boolean(),
    errors: z.array(z.string()),
  })
  .strict();
export type ImportRowView = z.infer<typeof importRowSchema>;

/**
 * US-E23.F4-0334-S11 (@UC9520) — CẢNH BÁO SỚM ở màn staging.
 *
 * 🔴 VẤN ĐỀ THẬT: `phone_only_no_channel` trước đây CHỈ lộ ra ở response CONFIRM — tức người dùng
 * biết "0 lời mời được gửi" SAU KHI đã bấm xác nhận. Mock Figma có batch **32 người chỉ có số điện
 * thoại**: thêm được hết, mà **không ai nhận được lời mời**, và màn hình không nói gì.
 *
 * 🔴 TÁI DÙNG `IMPORT_SKIP_REASON` — cảnh báo dùng ĐÚNG bộ mã mà confirm sẽ trả. Khai một enum
 * "warning" riêng sẽ cho phép màn staging nói một đằng và kết quả confirm ra một nẻo.
 */
export const importWarningSchema = z
  .object({
    reason: importSkipReasonSchema,
    /** Số row dính cảnh báo này. */
    count: z.number().int().positive(),
  })
  .strict();
export type ImportWarning = z.infer<typeof importWarningSchema>;

/** Response tạo/đọc batch staging. */
export const importBatchResponseSchema = z
  .object({
    batchId: z.string().uuid(),
    source: importSourceSchema,
    status: importBatchStatusSchema,
    rows: z.array(importRowSchema),
    validCount: z.number().int().nonnegative(),
    invalidCount: z.number().int().nonnegative(),
    /** S11: cảnh báo TRƯỚC khi bấm confirm. Rỗng = không có gì bất thường. */
    warnings: z.array(importWarningSchema),
    /**
     * 🔴 Số lời mời SẼ thực sự được gửi nếu bấm confirm ngay bây giờ (AC3).
     * Bằng 0 mà `validCount` > 0 là đúng ca Figma: mọi row hợp lệ nhưng KHÔNG ai có kênh nhận.
     * Trả con số này ra để màn hình nói được sự thật đó TRƯỚC, thay vì sau.
     */
    invitableCount: z.number().int().nonnegative(),
  })
  .strict();
export type ImportBatchResponse = z.infer<typeof importBatchResponseSchema>;

/** Một dòng trong report confirm. */
export const confirmResultItemSchema = z
  .object({
    rowId: z.string().uuid(),
    /** created = đã tạo Invitation pending; skipped = bỏ qua (kèm reason). */
    status: z.enum(["created", "skipped"]),
    reason: importSkipReasonSchema.nullable(),
  })
  .strict();
export type ConfirmResultItem = z.infer<typeof confirmResultItemSchema>;

/** Response POST confirm — report per-row + đếm. */
export const confirmImportResponseSchema = z
  .object({
    batchId: z.string().uuid(),
    status: importBatchStatusSchema,
    createdCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    results: z.array(confirmResultItemSchema),
  })
  .strict();
export type ConfirmImportResponse = z.infer<typeof confirmImportResponseSchema>;

/* ===========================================================================
 * US-E24.F11-0360-S03a (@UC9604) — GET /v1/groups/:id/member-imports.
 *
 * VÌ SAO CÓ: surface member-imports có post/get-theo-batchId/patch-row/delete-row/confirm và **0
 * method list**. `batchId` chỉ nằm trong bộ nhớ client ⇒ đóng app giữa chừng là bản nháp thành mồ
 * côi — mang theo email/phone của hàng chục phụ huynh, trong một bảng không ai nhìn tới.
 *
 * 🔴 LIST TRẢ METADATA + SỐ ĐẾM, KHÔNG NỘI DUNG ROW. `MemberImportRow.email`/`phone` là PII của
 * người **chưa hề đồng ý** tham gia (mới chỉ bị ai đó dán vào một file CSV). 20 batch × 50 dòng =
 * 1000 email trong MỘT response. Muốn chi tiết ⇒ `GET .../{batchId}` (đã có, đã có RBAC riêng).
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const IMPORT_BATCHES_MAX_LIMIT = 50;
export const IMPORT_BATCHES_MAX_CURSOR_LENGTH = 512;

/**
 * 1 dòng batch trong danh sách. `.strict()` + test khoá cứng danh sách field: thêm field mà quên
 * sửa hợp đồng ⇒ ĐỎ. Đó là chốt chặn duy nhất đứng giữa "thêm cho tiện" và "1000 email rời API".
 */
export const importBatchListItemSchema = z
  .object({
    batchId: z.string().uuid(),
    source: importSourceSchema,
    status: importBatchStatusSchema,
    /** Số đếm, KHÔNG phải nội dung — biết có bao nhiêu dòng là đủ để quyết định mở batch nào. */
    rowCount: z.number().int().min(0),
    validCount: z.number().int().min(0),
    createdAt: z.string(),
  })
  .strict();

export type ImportBatchListItem = z.infer<typeof importBatchListItemSchema>;

/** Envelope cursor chuẩn repo. Nhóm chưa từng nhập ⇒ `data: []` (KHÔNG 404). */
export const importBatchesListResponseSchema = z
  .object({
    data: z.array(importBatchListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type ImportBatchesListResponse = z.infer<typeof importBatchesListResponseSchema>;

/**
 * Query. `.strict()`: `status` ngoài whitelist ⇒ 400 tại biên, KHÔNG rơi vào nhánh "không lọc" rồi
 * im lặng trả **cả batch đã confirmed** cho một màn hình đang xin "bản nháp".
 */
export const importBatchesListQuerySchema = z
  .object({
    cursor: z.string().max(IMPORT_BATCHES_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(IMPORT_BATCHES_MAX_LIMIT).optional(),
    status: importBatchStatusSchema.optional(),
  })
  .strict();

export type ImportBatchesListQuery = z.infer<typeof importBatchesListQuerySchema>;
